import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { FaEnvelope, FaTimes, FaCheck } from 'react-icons/fa';
import './NotificationModal.css';

const NotificationModal = ({ isOpen, onClose, employeeArea, employeeId, onSendEmail }) => {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [needsTwoManagers, setNeedsTwoManagers] = useState(false);
  const [actualEmployeeArea, setActualEmployeeArea] = useState('');
  
  // First, fetch the current user's actual area from employees table
  useEffect(() => {
    const fetchEmployeeArea = async () => {
      if (!isOpen) return;
      
      try {
        // Get the logged in user
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) throw new Error('No user logged in');
        
        // Get the employee record
        const { data: employeeData, error: employeeError } = await supabase
          .from('employees')
          .select('area, wsp_email')
          .eq('wsp_email', user.email)
          .single();
          
        if (employeeError) throw employeeError;
        
        if (employeeData) {
          console.log('Found employee area:', employeeData.area);
          setActualEmployeeArea(employeeData.area);
        } else {
          throw new Error('Employee not found');
        }
      } catch (err) {
        console.error('Error fetching employee area:', err);
        setError('No se pudo determinar su área. Por favor intente más tarde.');
      }
    };
    
    fetchEmployeeArea();
  }, [isOpen]);
  
  // Check if the area requires two recipients (CEO + RH)
  useEffect(() => {
    const specialAreas = ["Administrativo / Dirección", "Comercial", "Ventas"];
    setNeedsTwoManagers(specialAreas.includes(actualEmployeeArea));
  }, [actualEmployeeArea]);
  
  // Fetch relevant managers based on employee area
  useEffect(() => {
    const fetchManagers = async () => {
      if (!actualEmployeeArea || !isOpen) return;
      
      setLoading(true);
      console.log('Fetching managers for area:', actualEmployeeArea);
      
      try {
        if (needsTwoManagers) {
          // Special case: need to fetch both CEO and RH role users
          const { data: ceoData, error: ceoError } = await supabase
            .from('users')
            .select('id, wsp_email, role')
            .eq('role', 'CEO');
            
          const { data: rhData, error: rhError } = await supabase
            .from('users')
            .select('id, wsp_email, role')
            .eq('role', 'RH');
            
          if (ceoError) console.error('CEO fetch error:', ceoError);
          if (rhError) console.error('RH fetch error:', rhError);
          if (ceoError && rhError) throw new Error('Failed to fetch managers');
          
          // Combine and label the data
          const combinedData = [
            ...(ceoData || []).map(user => ({ ...user, roleGroup: 'CEO' })),
            ...(rhData || []).map(user => ({ ...user, roleGroup: 'RH' }))
          ];
          
          console.log('Found managers:', combinedData);
          setManagers(combinedData);
        } else {
          // Normal case: fetch users with role matching employee's area
          console.log('Fetching users with role matching area:', actualEmployeeArea);
          const { data, error } = await supabase
            .from('users')
            .select('id, wsp_email, role')
            .eq('role', actualEmployeeArea);
            
          if (error) {
            console.error('Error fetching by role:', error);
            throw error;
          }
          
          console.log('Found managers:', data);
          setManagers(data || []);
        }
      } catch (err) {
        console.error('Error fetching managers:', err);
        setError('No se pudieron cargar los contactos. Por favor intente más tarde.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchManagers();
  }, [isOpen, actualEmployeeArea, needsTwoManagers]);
  
  const handleSelectManager = (manager) => {
    if (needsTwoManagers) {
      // For special areas, handle selection of two managers (one CEO, one RH)
      const managerExists = selectedManagers.find(m => m.id === manager.id);
      
      if (managerExists) {
        // Remove if already selected
        setSelectedManagers(selectedManagers.filter(m => m.id !== manager.id));
      } else {
        // Add new manager, but ensure we have at most one of each role group
        const otherManagers = selectedManagers.filter(m => m.roleGroup !== manager.roleGroup);
        setSelectedManagers([...otherManagers, manager]);
      }
    } else {
      // For regular areas, just select one manager
      setSelectedManagers([manager]);
    }
  };
  
  const handleSendEmail = () => {
    if (selectedManagers.length === 0) return;
    
    // Get email addresses from selected managers
    const managerEmails = selectedManagers.map(manager => manager.wsp_email);
    
    // Log the emails for debugging
    console.log('Sending email to:', managerEmails);
    
    // Call the onSendEmail prop with the emails
    onSendEmail(managerEmails);
    
    // Close the modal
    onClose();
  };
  
  // Check if send button should be disabled
  const isSendDisabled = needsTwoManagers 
    ? selectedManagers.length < 2 || !selectedManagers.some(m => m.roleGroup === 'CEO') || !selectedManagers.some(m => m.roleGroup === 'RH')
    : selectedManagers.length === 0;

  // If modal is not open, don't render anything
  if (!isOpen) return null;
  
  return (
    <div className="notification-modal-overlay">
      <div className="notification-modal-container">
        <div className="notification-modal-header">
          <h2>Seleccionar destinatario</h2>
          <button onClick={onClose} className="notification-modal-close">
            <FaTimes />
          </button>
        </div>
        
        <div className="notification-modal-body">
          {loading ? (
            <div className="notification-modal-loading">
              <p>Cargando contactos...</p>
            </div>
          ) : error ? (
            <div className="notification-modal-error">
              <p>{error}</p>
            </div>
          ) : (
            <>
              {actualEmployeeArea && (
                <div className="notification-modal-area-info">
                  <p>Contactos para área: <strong>{actualEmployeeArea}</strong></p>
                </div>
              )}
            
              {needsTwoManagers && (
                <div className="notification-modal-instructions">
                  <p>Por favor selecciona un contacto de cada categoría:</p>
                </div>
              )}
              
              {needsTwoManagers && (
                <div className="notification-modal-sections">
                  <div className="notification-modal-section">
                    <h3>Dirección General (CEO)</h3>
                    <ul className="notification-modal-contacts">
                      {managers.filter(m => m.roleGroup === 'CEO').map(manager => (
                        <li 
                          key={manager.id}
                          className={`notification-modal-contact ${selectedManagers.some(m => m.id === manager.id) ? 'selected' : ''}`}
                          onClick={() => handleSelectManager(manager)}
                        >
                          <div className="notification-modal-contact-info">
                            <span className="notification-modal-contact-name">{manager.wsp_email}</span>
                          </div>
                          {selectedManagers.some(m => m.id === manager.id) && (
                            <FaCheck className="notification-modal-check" />
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="notification-modal-section">
                    <h3>Recursos Humanos (RH)</h3>
                    <ul className="notification-modal-contacts">
                      {managers.filter(m => m.roleGroup === 'RH').map(manager => (
                        <li 
                          key={manager.id}
                          className={`notification-modal-contact ${selectedManagers.some(m => m.id === manager.id) ? 'selected' : ''}`}
                          onClick={() => handleSelectManager(manager)}
                        >
                          <div className="notification-modal-contact-info">
                            <span className="notification-modal-contact-name">{manager.wsp_email}</span>
                          </div>
                          {selectedManagers.some(m => m.id === manager.id) && (
                            <FaCheck className="notification-modal-check" />
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              
              {!needsTwoManagers && (
                <ul className="notification-modal-contacts">
                  {managers.length === 0 ? (
                    <li className="notification-modal-no-contacts">
                      No se encontraron contactos para tu área
                    </li>
                  ) : (
                    managers.map(manager => (
                      <li 
                        key={manager.id}
                        className={`notification-modal-contact ${selectedManagers.some(m => m.id === manager.id) ? 'selected' : ''}`}
                        onClick={() => handleSelectManager(manager)}
                      >
                        <div className="notification-modal-contact-info">
                          <span className="notification-modal-contact-name">{manager.wsp_email}</span>
                        </div>
                        {selectedManagers.some(m => m.id === manager.id) && (
                          <FaCheck className="notification-modal-check" />
                        )}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </>
          )}
        </div>
        
        <div className="notification-modal-footer">
          <button onClick={onClose} className="notification-modal-cancel">
            Cancelar
          </button>
          <button 
            onClick={handleSendEmail} 
            className="notification-modal-send"
            disabled={isSendDisabled}
          >
            <FaEnvelope className="notification-modal-send-icon" /> 
            Enviar correo automático
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationModal;
